import { NewsdeskApiError } from './errors'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/**
 * One HTTP exchange as the SDK hands it to whatever does the networking.
 * `url` is the path with its query string already serialized, relative to the
 * API origin. `options` is opaque to the SDK and reaches the transport as is,
 * so a host app can pass its own concerns (a cache policy, a retry flag)
 * through SDK calls without the SDK knowing about them.
 */
export interface TransportRequest {
  method: HttpMethod
  url: string
  headers?: Record<string, string>
  body?: unknown
  signal?: AbortSignal
  options?: Record<string, unknown>
}

/**
 * Resolves with the parsed response body (null for an empty one) and rejects
 * with a NewsdeskApiError on a non-2xx status.
 */
export type Transport = (request: TransportRequest) => Promise<unknown>

export interface FetchTransportConfig {
  /** API origin, e.g. https://newsdesk-api.scrippsnet.io */
  baseUrl: string
  /** Bearer token for each request, or nothing when the host sends cookies. */
  getToken?: () => string | null | undefined | Promise<string | null | undefined>
  /** Headers added to every request. */
  headers?: Record<string, string>
  /** fetch implementation; defaults to the global one. */
  fetch?: typeof fetch
  /** Credentials mode for fetch; `include` when the API authenticates by cookie. */
  credentials?: RequestCredentials
}

/** The default transport: plain fetch with JSON in and out. */
export function createFetchTransport({ baseUrl, getToken, headers = {}, fetch: fetchImpl, credentials }: FetchTransportConfig): Transport {
  const doFetch = fetchImpl ?? globalThis.fetch?.bind(globalThis)
  if (!doFetch) throw new Error('createFetchTransport: no fetch implementation available; pass one in config.fetch')
  const origin = baseUrl.replace(/\/+$/, '')

  return async ({ method, url, headers: requestHeaders = {}, body, signal }) => {
    const token = await getToken?.()
    const response = await doFetch(`${origin}${url.startsWith('/') ? url : `/${url}`}`, {
      method,
      signal,
      credentials,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
        ...requestHeaders
      },
      body: body === undefined ? undefined : JSON.stringify(body)
    })

    const parsed = await parseBody(response)
    if (!response.ok) {
      throw new NewsdeskApiError({ status: response.status, body: parsed, url })
    }
    return parsed
  }
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}
